import {
  generalGetLiveLogs,
  getStreamAdFilledInVideoUnitQuery,
  getStreamAdFilledQuery,
  getStreamPodFilledQuery,
  getStreamPodRequestQuery,
  getLiveStreamWatcherAdBreakQuery,
} from '~/src/dash/api/live_logs/controller';
import {
  IStreamAdFilledData,
  IStreamAdFilledInVideoUnitData,
  IStreamPodFilledData,
  IStreamPodRequestData,
  ILiveStreamWatcherAdBreakData,
} from '~/src/dash/api/live_logs/models';
import { createUrl, restDelete, restPost } from '~/src/dash/api/rest';

export interface INetworkChannelLiveLogsWrapper {
  podRequest: IStreamPodRequestData[];
  podFilled: IStreamPodFilledData[];
  adFilled: IStreamAdFilledData[];
  inVideoUnit: IStreamAdFilledInVideoUnitData[];
  liveStreamWatcherAdBreak: ILiveStreamWatcherAdBreakData[];
}

export interface IFlagSuccess {
  success: boolean;
  message: string;
}
export interface IFlagVideoStatus {
  videoURL: string;
  status: boolean;
  flaggedFields: string;
  corrections: string;
}

export async function GetNetworkChannelLiveLogsData(
  networkChannelID: number,
  publisherID: string,
  username: string,
  lvcStreamID: string,
): Promise<INetworkChannelLiveLogsWrapper> {
  const out = {} as INetworkChannelLiveLogsWrapper;

  // Pod Request
  const streamPodRequestQuery = getStreamPodRequestQuery(networkChannelID);
  const podRequestPromise = generalGetLiveLogs<IStreamPodRequestData[]>(
    streamPodRequestQuery,
    publisherID,
    username,
  ).then(([data, err]) => {
    if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
      out.podRequest = data;
    }
    if (err !== undefined) {
      console.error(err);
    }
  });

  // Pod Filled
  const streamPodFilledQuery = getStreamPodFilledQuery(networkChannelID);
  const podFilledPromise = generalGetLiveLogs<IStreamPodFilledData[]>(
    streamPodFilledQuery,
    publisherID,
    username,
  ).then(([data, err]) => {
    if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
      out.podFilled = data;
    }
    if (err !== undefined) {
      console.error(err);
    }
  });

  // Ad Filled
  const streamAdFilledQuery = getStreamAdFilledQuery(networkChannelID);
  const adFilledPromise = generalGetLiveLogs<IStreamAdFilledData[]>(
    streamAdFilledQuery,
    publisherID,
    username,
  ).then(([data, err]) => {
    if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
      out.adFilled = data;
    }
    if (err !== undefined) {
      console.error(err);
    }
  });

  // LiveStreamWatcherAdBreak
  const liveStreamWatcherAdBreakQuery = getLiveStreamWatcherAdBreakQuery(lvcStreamID);
  const liveStreamWatcherAdBreakPromise = generalGetLiveLogs<ILiveStreamWatcherAdBreakData[]>(
    liveStreamWatcherAdBreakQuery,
    publisherID,
    username,
  ).then(([data, err]) => {
    if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
      out.liveStreamWatcherAdBreak = data;
    }
    if (err !== undefined) {
      console.error(err);
    }
  });

  await Promise.all([
    podRequestPromise,
    podFilledPromise,
    adFilledPromise,
    liveStreamWatcherAdBreakPromise,
  ]);
  return out;
}

export interface IInVideoUnitLiveLogsWrapper {
  inVideoUnit: IStreamAdFilledInVideoUnitData[];
}

export async function GetInVideoUnitLiveLogsData(
  inVideoUnitID: number,
  publisherID: string,
  username: string,
): Promise<IInVideoUnitLiveLogsWrapper> {
  const out = {} as IInVideoUnitLiveLogsWrapper;

  const inVideoUnitQuery = getStreamAdFilledInVideoUnitQuery(inVideoUnitID);
  const inVideoUnitPromise = generalGetLiveLogs<IStreamAdFilledInVideoUnitData[]>(
    inVideoUnitQuery,
    publisherID,
    username,
  ).then(([data, err]) => {
    if (data !== undefined && data !== null && Object.keys(data).length !== 0) {
      out.inVideoUnit = data;
    }
    if (err !== undefined) {
      console.error(err);
    }
  });

  await Promise.all([inVideoUnitPromise]);
  return out;
}

export async function flagLiveStreamWatcherAdBreak(
  publisherID: string,
  siteID: number,
  videoURL: string,
  garmScores: string,
  personality: string,
  fieldsToFlag: string[],
  corrections: string,
  userName: string,
): Promise<IFlagSuccess | undefined> {
  let reqUrl = createUrl('/flag_livestream_correction', publisherID, 'v1');
  reqUrl += `&site_id=${siteID}&video_url=${videoURL}&garm_scores=${garmScores}&personalities=${personality}&fields_to_flag=${fieldsToFlag}&corrections=${corrections}&user_name=${userName}`;
  try {
    const res = await restPost<IFlagSuccess>(reqUrl, {});
    return res.parsedBody;
  } catch (e) {
    return {
      success: false,
      message: 'Error flagging livestream correction',
    };
  }
}

export async function getFlagLiveStreamStatus(
  publisherID: string,
  videoURLs: string[],
): Promise<IFlagVideoStatus[] | undefined> {
  const reqUrl = createUrl('/flag_livestream_status', publisherID, 'v1');
  try {
    const res = await restPost<IFlagVideoStatus[]>(reqUrl, { videoURLs });
    return res.parsedBody;
  } catch (e) {
    console.error('Error getting flag livestream status:', e);
    return undefined;
  }
}
